import React, { useState, useEffect } from 'react';
import RadioButton from 'components/Generic/Forms/FormFields/RadioButton';
import FormElemWrapper from 'components/Generic/Forms/FormFields/FormElemWrapper';
import classNames from 'classnames';
import { Option } from 'model/Form';
import styles from './RadioButtonFieldset.module.scss';

type RadioButtonFieldsetProps = {
  fields: {
    style?: {
      listOrder: string;
    };
    conditionalField?: boolean;
    fieldDescription?: string;
    instructions?: string;
    defaultValue: string;
    id: string;
    validations?: any[];
    name?: string;
    optionList: {
      elements: Option[];
    };
  };
  className?: string;
  onUpdateValue: (value: string) => void;
};

const RadioButtonFieldset = ({
  fields,
  onUpdateValue,
  className,
  changeButtonsGrid,
}: RadioButtonFieldsetProps) => {
  const [pickedValue, setPickedValue] = useState('');
  const [isError, setIsError] = useState(false);

  const buttons = fields?.optionList?.elements;
  const isRequired =
    fields?.validations?.some(({ condition }) => condition.type === 'IsRequiredCondition') ?? false;
  const getName = isRequired && fields?.name ? fields?.name + '*' : fields?.name;
  const getInstruction: string | undefined = fields?.instructions;
  const getFieldDescription: string | undefined = fields?.fieldDescription;
  const getId: string | undefined = fields?.id;
  const isConditional: boolean | undefined = fields?.conditionalField;
  const requiredErrorMessage: string = fields?.validations?.find(
    ({ condition }) => condition.type === 'IsRequiredCondition'
  )?.message;

  const getColumnSize = (): number => {
    switch (fields?.style?.listOrder) {
      case 'twoColumn':
        return 6;
      case 'threeColumn':
        return 4;
      default:
        return 12;
    }
  };
  const getColClass = (): string => {
    return 'col-md-' + getColumnSize();
  };
  const setState = (value: string): void => {
    setPickedValue(value);
    setIsError(false);
    onUpdateValue(value);
  };
  const checkError = (event: React.InvalidEvent<HTMLInputElement>): void => {
    if (event.target.validity.valueMissing) {
      setIsError(true);
    } else {
      setIsError(false);
    }
  };

  useEffect(() => {
    if (fields?.defaultValue) {
      setPickedValue(fields.defaultValue);
    }
  }, [fields?.defaultValue]);

  return (
    <FormElemWrapper
      className={classNames('form-element--input', {
        'form-element--filled': !!pickedValue,
        'conditional-field': isConditional,
        [className]: true,
      })}
      isError={isError}
      slots={{
        fieldLabel: getName ? <>{getName}</> : null,
        description: getFieldDescription ? (
          <p className="text-grey mt-2">{getFieldDescription}</p>
        ) : null,
        error: isError ? <>{requiredErrorMessage}</> : null,
        hint: getInstruction ? <>{getInstruction}</> : null,
      }}
      wrapperTag="div"
    >
      <div className="row">
        <div className={`radioFieldSet ${changeButtonsGrid ? styles.radioFieldSet__flex : ''}`}>
          {buttons.map((button, i) => (
            <RadioButton
              dataId={fields.id}
              changeButtonsGrid={changeButtonsGrid}
              key={button.value}
              value={button.value}
              required={isRequired}
              modelValue={pickedValue}
              className={`${getColClass()} ${i !== 0 ? 'addMargin' : ''}`}
              name={getId}
              onChange={setState}
              onError={checkError}
              label={button.displayName}
            />
          ))}
        </div>
      </div>
    </FormElemWrapper>
  );
};

export default RadioButtonFieldset;
